'use client'

import { usePathname } from 'next/navigation'
import { Suspense } from 'react'

/**
 * Routes rendered full-width (no mobile-frame constraint).
 * Useful for internal tooling, debug pages, or admin views.
 */
const FULL_WIDTH_PREFIXES = ['/push-test', '/dev/push', '/splash']

function isFullWidth(pathname: string | null): boolean {
	if (!pathname) {return false}
	return FULL_WIDTH_PREFIXES.some(
		(prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
	)
}

/**
 * AppShell — top-level frame para la PWA.
 *
 * Móvil   : h-dvh exacto (430 px max) — igual que el div raíz anterior.
 *           Necesario para que los descendientes con h-full (chat, private
 *           layout) tengan una altura definida y los scrolls funcionen.
 *
 * Desktop : tarjeta centrada con h-full dentro de h-dvh, bordes redondeados,
 *           sombra y padding (sm:p-4 / md:p-8).
 *
 * Full-width: sin restricción, llena el viewport (push-test, splash).
 */
export function AppShell({ children }: { children: React.ReactNode }) {
	const pathname = usePathname()
	const fullWidth = isFullWidth(pathname)

	if (fullWidth) {
		return (
			<div className="min-h-dvh w-full bg-white">
				<Suspense>{children}</Suspense>
			</div>
		)
	}

	return (
		// h-dvh en el contenedor raíz → los h-full de los hijos resuelven correctamente
		<div className="flex h-dvh w-full justify-center sm:p-4 md:p-8">
			<div className="relative flex h-full w-full max-w-[430px] flex-col overflow-x-hidden bg-white shadow-none sm:rounded-2xl sm:shadow-xl sm:ring-1 sm:ring-black/5">
				<Suspense>{children}</Suspense>
			</div>
		</div>
	)
}
